import { useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck, Clock, XCircle } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { AddressFields, EMPTY_ADDRESS, addressComplete } from "@/components/AddressFields";
import { DocumentUploader } from "@/components/dashboard/DocumentUploader";
import { validCnpj, validCpf, maskCnpj, maskCpf, maskPhone, digits } from "@/lib/brdocs";

function Status({ status }) {
  const map = {
    approved: [BadgeCheck, "Selo de Validação ativo", "Sua instituição é verificada e pode gerenciar espaços.", "text-emerald-400 border-emerald-500/40"],
    pending: [Clock, "Em análise", "Recebemos suas credenciais. A equipe Koiné está revisando.", "text-amber-300 border-amber-500/40"],
    rejected: [XCircle, "Não aprovado", "Revise os dados e envie novamente.", "text-red-400 border-red-500/40"],
  };
  const m = map[status];
  if (!m) return null;
  const [Icon, title, text, cls] = m;
  return (
    <div className={`flex gap-3 rounded-2xl border bg-card p-4 ${cls}`} data-testid={`verification-status-${status}`}>
      <Icon className="shrink-0" />
      <div><p className="font-bold">{title}</p><p className="text-sm text-muted-foreground">{text}</p></div>
    </div>
  );
}

function DocField({ label, value, onChange, mask, valid, testid, placeholder }) {
  const d = digits(value);
  const full = mask === maskCnpj ? 14 : 11;
  const bad = d.length === full && !valid(value);
  const good = d.length === full && valid(value);
  return (
    <div className="space-y-1.5">
      <Label className="text-xs text-muted-foreground">{label}</Label>
      <Input required inputMode="numeric" value={value} placeholder={placeholder} onChange={(e) => onChange(mask(e.target.value))}
        className={`h-11 rounded-xl bg-white/5 ${bad ? "border-red-500 focus-visible:ring-red-500" : good ? "border-emerald-500/60" : ""}`} data-testid={testid} />
      {bad && <p className="text-[11px] text-red-400" data-testid={`${testid}-error`}>Número inválido: os dígitos verificadores não conferem</p>}
    </div>
  );
}

const Section = ({ title, children }) => (
  <fieldset className="space-y-3 rounded-2xl border border-white/5 bg-card/60 p-4 sm:col-span-2">
    <legend className="px-1 text-xs font-bold uppercase tracking-[0.15em] text-[hsl(var(--gold))]">{title}</legend>
    {children}
  </fieldset>
);

const TextF = ({ label, testid, ...p }) => (
  <div className="space-y-1.5">
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <Input required className="h-11 rounded-xl bg-white/5" data-testid={testid} {...p} />
  </div>
);

function VerificationForm({ user, onSent }) {
  const [f, setF] = useState({ legal_name: "", cnpj: "", denomination: user.denomination || "evangelica", leader_name: user.leader_name || "", leader_cpf: "", leader_role: "", contact_phone: "", notes: "" });
  const [address, setAddress] = useState({ ...EMPTY_ADDRESS, ...(user.address || {}) });
  const [docs, setDocs] = useState([]);
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!validCnpj(f.cnpj)) return toast.error("CNPJ inválido: confira os dígitos verificadores");
    if (!validCpf(f.leader_cpf)) return toast.error("CPF do responsável inválido");
    if (!addressComplete(address)) return toast.error("Preencha o endereço completo com CEP e UF");
    if (docs.some((d) => !d.public_id)) return toast.error("Aguarde o envio dos documentos terminar");
    if (!docs.length) return toast.error("Envie pelo menos um documento comprobatório");
    setSending(true);
    try {
      await api.post("/institution/verification", { ...f, address, documents: docs.map((d) => ({ public_id: d.public_id, name: d.name })) });
      toast.success("Credenciais enviadas para análise");
      onSent();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={submit} className="grid gap-4 sm:grid-cols-2" data-testid="verification-form">
      <Section title="Instituição">
        <div className="grid gap-3 sm:grid-cols-2">
          <TextF label="Razão social" value={f.legal_name} onChange={set("legal_name")} testid="verification-legal_name-input" />
          <DocField label="CNPJ" value={f.cnpj} onChange={(v) => setF({ ...f, cnpj: v })} mask={maskCnpj} valid={validCnpj} placeholder="00.000.000/0000-00" testid="verification-cnpj-input" />
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Tradição</Label>
            <Select value={f.denomination} onValueChange={(v) => setF({ ...f, denomination: v })}>
              <SelectTrigger className="h-11 rounded-xl bg-white/5" data-testid="verification-denomination-select"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="evangelica">Evangélica</SelectItem><SelectItem value="catolica">Católica</SelectItem></SelectContent>
            </Select>
          </div>
          <TextF label="Telefone institucional" inputMode="tel" value={f.contact_phone} onChange={(e) => setF({ ...f, contact_phone: maskPhone(e.target.value) })} testid="verification-contact_phone-input" />
        </div>
      </Section>
      <Section title="Endereço completo"><AddressFields value={address} onChange={setAddress} prefix="verification-address" /></Section>
      <Section title="Responsável">
        <div className="grid gap-3 sm:grid-cols-3">
          <TextF label="Nome do líder" value={f.leader_name} onChange={set("leader_name")} testid="verification-leader_name-input" />
          <DocField label="CPF do responsável" value={f.leader_cpf} onChange={(v) => setF({ ...f, leader_cpf: v })} mask={maskCpf} valid={validCpf} placeholder="000.000.000-00" testid="verification-leader_cpf-input" />
          <TextF label="Função (ex: Pastor, Pároco)" value={f.leader_role} onChange={set("leader_role")} testid="verification-leader_role-input" />
        </div>
      </Section>
      <Section title="Documentos comprobatórios"><DocumentUploader docs={docs} setDocs={setDocs} /></Section>
      <div className="space-y-1.5 sm:col-span-2">
        <Label className="text-xs text-muted-foreground">Observações</Label>
        <Textarea value={f.notes} onChange={set("notes")} data-testid="verification-notes-input" />
      </div>
      <Button type="submit" disabled={sending} className="h-11 rounded-full sm:col-span-2" data-testid="verification-submit-button">{sending ? "Enviando…" : "Enviar para validação"}</Button>
    </form>
  );
}

export function VerificationPanel() {
  const { user, refreshMe } = useAuth();
  const [status, setStatus] = useState(user.verification_status);
  useEffect(() => { api.get("/institution/verification").then((r) => r.data && setStatus(r.data.status)); }, []);
  const sent = () => { setStatus("pending"); refreshMe(); };

  return (
    <div className="max-w-3xl space-y-5 pb-24 md:pb-0">
      <Status status={user.verified ? "approved" : status} />
      {!user.verified && status !== "pending" && <VerificationForm user={user} onSent={sent} />}
    </div>
  );
}
